import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
  signal,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextarea } from 'primeng/inputtextarea';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { BuscadorMunicipioComponent } from '../../../shared/buscadores/buscador-municipio.component';
import { BuscadorPoblacionComponent } from '../../../shared/buscadores/buscador-poblacion.component';
import { Municipio } from 'src/app/domain/model/municipio/municipio';
import { Poblacion } from 'src/app/domain/model/transporte/poblacion';
import { MessageService } from 'primeng/api';
import { sanitizeTextarea } from 'src/app/common/input-utils';

/**
 * Datos del panel de transporte
 * Legacy: mostrarOcultarTransporte() en CrearCitaAdministrativaViewImpl.java
 */
export interface TransporteData {
  cdmunicipioOrigen: string;
  dsmunicipioOrigen: string;
  cdsuraOrigen: string; // Código SURA para búsqueda en W094_SGS_TARIFAS_TRANSPORTE
  cdmunicipioEntrega: string;
  dsmunicipioEntrega: string;
  cdsuraEntrega: string; // Código SURA para búsqueda en W094_SGS_TARIFAS_TRANSPORTE
  cdpoblacion: string;
  dspoblacion: string;
  snpagoTransporteEmpleado: 'S' | 'N';
  ptvalorTransporte: number | null;
  snpagoTransporteAdicional: 'S' | 'N';
  ptvalorTransporteAdicional: number | null;
  dsobservacionTransporteAdicional: string;
}

/**
 * Componente: Panel de Transporte
 *
 * Panel con campos de transporte para citas de TUTORIA_CAMPO (41) y TUTORIA_PEC_PAI (40)
 * cuando Atención Virtual = NO.
 *
 * Legacy: mostrarOcultarTransporte() en CrearCitaAdministrativaViewImpl.java
 * - Municipio de Origen (lupa -> TPSC_MUNICIPIOS)
 * - Municipio de Entrega (lupa -> TPSC_MUNICIPIOS)
 * - Poblacion (lupa -> W094_SGS_TARIFAS_TRANSPORTE, requiere ambos municipios)
 * - Pago Transporte a Empleado (S/N)
 * - Valor Transporte (readonly, calculado desde tarifa)
 * - Pago Transporte Adicional (S/N)
 * - Valor Transporte Adicional (editable)
 * - Observaciones Transporte Adicional
 *
 * Uso:
 * ```html
 * <app-panel-transporte
 *   [data]="transporteData"
 *   (dataChange)="onTransporteChange($event)" />
 * ```
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-panel-transporte',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    InputNumberModule,
    InputTextarea,
    RadioButtonModule,
    ButtonModule,
    TooltipModule,
    BuscadorMunicipioComponent,
    BuscadorPoblacionComponent
  ],
  host: { class: 'col-span-2' },
  template: `
    <!-- Fila 1: Municipio origen + Municipio entrega -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium text-gray-600 mb-1">
          Municipio de origen <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-2">
          <input
            pInputText
            [value]="data.dsmunicipioOrigen"
            placeholder="Seleccione municipio de origen"
            readonly
            class="flex-1 cursor-pointer bg-gray-50"
            (click)="buscadorMunicipioOrigen.abrir()"
          />
          <button
            pButton
            icon="pi pi-search"
            class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
            pTooltip="Buscar municipio"
            (click)="buscadorMunicipioOrigen.abrir()"
          ></button>
        </div>
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-600 mb-1">
          Municipio de entrega <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-2">
          <input
            pInputText
            [value]="data.dsmunicipioEntrega"
            placeholder="Seleccione municipio de entrega"
            readonly
            class="flex-1 cursor-pointer bg-gray-50"
            (click)="buscadorMunicipioEntrega.abrir()"
          />
          <button
            pButton
            icon="pi pi-search"
            class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
            pTooltip="Buscar municipio"
            (click)="buscadorMunicipioEntrega.abrir()"
          ></button>
        </div>
      </div>
    </div>

    <!-- Fila 2: Población + espacio vacío -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium text-gray-600 mb-1">
          Población <span class="text-red-500">*</span>
        </label>
        <div class="flex gap-2">
          <input
            pInputText
            [value]="poblacionDisplay()"
            placeholder="Seleccione población"
            readonly
            class="flex-1 cursor-pointer bg-gray-50"
            [class.opacity-50]="!data.cdsuraOrigen || !data.cdsuraEntrega"
            (click)="buscarPoblacion()"
          />
          <button
            pButton
            icon="pi pi-search"
            class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
            pTooltip="Buscar población"
            [disabled]="!data.cdsuraOrigen || !data.cdsuraEntrega"
            (click)="buscarPoblacion()"
          ></button>
        </div>
        @if (!data.cdsuraOrigen || !data.cdsuraEntrega) {
          <small class="text-gray-500">Seleccione primero los municipios</small>
        }
      </div>
      <!-- Espacio vacío para alinear -->
      <div></div>
    </div>

    <!-- Fila 3: Pago transporte empleado S/N + Valor transporte -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium text-gray-600 mb-1"
          >Pago transporte a empleado</label
        >
        <div class="flex gap-4 mt-1">
          <div class="flex items-center">
            <p-radioButton
              name="snpagoTransporteEmpleado"
              value="S"
              [(ngModel)]="data.snpagoTransporteEmpleado"
              inputId="pagoTransSi"
              (ngModelChange)="onPagoTransporteEmpleadoChange()"
            ></p-radioButton>
            <label for="pagoTransSi" class="ml-2">Sí</label>
          </div>
          <div class="flex items-center">
            <p-radioButton
              name="snpagoTransporteEmpleado"
              value="N"
              [(ngModel)]="data.snpagoTransporteEmpleado"
              inputId="pagoTransNo"
              (ngModelChange)="onPagoTransporteEmpleadoChange()"
            ></p-radioButton>
            <label for="pagoTransNo" class="ml-2">No</label>
          </div>
        </div>
      </div>
      @if (data.snpagoTransporteEmpleado === 'S') {
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1"
            >Valor transporte</label
          >
          <p-inputNumber
            [(ngModel)]="data.ptvalorTransporte"
            mode="currency"
            currency="COP"
            locale="es-CO"
            [readonly]="true"
            [inputStyle]="{ 'background-color': '#f3f4f6' }"
            styleClass="w-full"
          >
          </p-inputNumber>
        </div>
      }
    </div>

    <!-- Fila 4: Pago transporte adicional S/N + Valor transporte adicional (solo si = S) -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <label class="block text-sm font-medium text-gray-600 mb-1"
          >Pago transporte adicional</label
        >
        <div class="flex gap-4 mt-1">
          <div class="flex items-center">
            <p-radioButton
              name="snpagoTransporteAdicional"
              value="S"
              [(ngModel)]="data.snpagoTransporteAdicional"
              inputId="pagoTransAdicSi"
              (ngModelChange)="onTransporteAdicionalChange()"
            ></p-radioButton>
            <label for="pagoTransAdicSi" class="ml-2">Sí</label>
          </div>
          <div class="flex items-center">
            <p-radioButton
              name="snpagoTransporteAdicional"
              value="N"
              [(ngModel)]="data.snpagoTransporteAdicional"
              inputId="pagoTransAdicNo"
              (ngModelChange)="onTransporteAdicionalChange()"
            ></p-radioButton>
            <label for="pagoTransAdicNo" class="ml-2">No</label>
          </div>
        </div>
      </div>
      @if (data.snpagoTransporteAdicional === 'S') {
        <div>
          <label class="block text-sm font-medium text-gray-600 mb-1"
            >Valor transporte adicional</label
          >
          <p-inputNumber
            [(ngModel)]="data.ptvalorTransporteAdicional"
            mode="currency"
            currency="COP"
            locale="es-CO"
            styleClass="w-full"
            (ngModelChange)="emitChange()"
          >
          </p-inputNumber>
        </div>
      }
    </div>

    <!-- Fila 5: Observaciones transporte adicional (solo si adicional = S) -->
    @if (data.snpagoTransporteAdicional === 'S') {
      <div class="md:col-span-2">
        <label class="block text-sm font-medium text-gray-600 mb-1"
          >Observaciones transporte adicional</label
        >
        <textarea
          pInputTextarea
          [(ngModel)]="data.dsobservacionTransporteAdicional"
          rows="2"
          class="w-full"
          (ngModelChange)="
            data.dsobservacionTransporteAdicional = sanitizeTextarea(
              data.dsobservacionTransporteAdicional
            );
            emitChange()
          "
        >
        </textarea>
      </div>
    }

    <!-- Buscadores de Municipio (diálogos) -->
    <app-buscador-municipio
      #buscadorMunicipioOrigen
      (municipioSeleccionado)="onMunicipioOrigenSeleccionado($event)"
    />

    <app-buscador-municipio
      #buscadorMunicipioEntrega
      (municipioSeleccionado)="onMunicipioEntregaSeleccionado($event)"
    />

    <app-buscador-poblacion
      #buscadorPoblacion
      [cdsuraOrigen]="data.cdsuraOrigen"
      [cdsuraDestino]="data.cdsuraEntrega"
      (poblacionSeleccionada)="onPoblacionSeleccionada($event)"
    />
  `
})
export class PanelTransporteComponent implements OnChanges {
  private readonly messageService = inject(MessageService);

  /** Sanitización XSS/SQLi (seguridad-girop §3) para el textarea de observaciones */
  sanitizeTextarea = sanitizeTextarea;

  @ViewChild('buscadorMunicipioOrigen')
  buscadorMunicipioOrigen!: BuscadorMunicipioComponent;
  @ViewChild('buscadorMunicipioEntrega')
  buscadorMunicipioEntrega!: BuscadorMunicipioComponent;
  @ViewChild('buscadorPoblacion')
  buscadorPoblacion!: BuscadorPoblacionComponent;

  /** Datos del transporte (two-way binding) */
  @Input() data: TransporteData = {
    cdmunicipioOrigen: '',
    dsmunicipioOrigen: '',
    cdsuraOrigen: '',
    cdmunicipioEntrega: '',
    dsmunicipioEntrega: '',
    cdsuraEntrega: '',
    cdpoblacion: '',
    dspoblacion: '',
    snpagoTransporteEmpleado: 'N',
    ptvalorTransporte: null,
    snpagoTransporteAdicional: 'N',
    ptvalorTransporteAdicional: null,
    dsobservacionTransporteAdicional: ''
  };

  /** Emite cuando cambian los datos */
  @Output() dataChange = new EventEmitter<TransporteData>();

  /** Tarifa guardada de la población seleccionada (para calcular cuando Pago = SI) */
  private tarifaPoblacion: number | null = null;

  /**
   * Inicializa tarifaPoblacion cuando se reciben datos del backend (modo edición).
   * Sin esto, al editar una cita el valor de transporte se pierde.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data.ptvalorTransporte !== null) {
      // En modo edición, el backend ya trae ptvalorTransporte calculado
      // Lo guardamos como tarifaPoblacion para que no se pierda
      this.tarifaPoblacion = this.data.ptvalorTransporte;
    }
  }

  /**
   * Muestra población en formato "código - nombre" como el legacy.
   * Legacy: tbPoblacion.getValue() contiene "124 - RIO CLARO"
   */
  poblacionDisplay(): string {
    if (this.data.cdpoblacion && this.data.dspoblacion) {
      return `${this.data.cdpoblacion} - ${this.data.dspoblacion}`;
    }
    return '';
  }

  /**
   * Maneja selección de municipio de origen.
   * Legacy: siBuscarMunicipioTransporte -> selecciona en TPSC_MUNICIPIOS
   */
  onMunicipioOrigenSeleccionado(municipio: Municipio): void {
    this.data.cdmunicipioOrigen = municipio.cdmunicipio || '';
    this.data.dsmunicipioOrigen = municipio.dsmunicipio || '';
    this.data.cdsuraOrigen = municipio.cdsura || ''; // Guardar CDSURA para búsqueda de poblaciones
    // Limpiar población al cambiar municipio
    this.limpiarPoblacion();
    this.emitChange();
    this.messageService.add({
      severity: 'success',
      summary: 'Municipio origen seleccionado',
      detail: municipio.dsmunicipio || '',
      life: 2000
    });
  }

  /**
   * Maneja selección de municipio de entrega.
   * Legacy: siBuscarMunicipioEntrega -> selecciona en TPSC_MUNICIPIOS
   */
  onMunicipioEntregaSeleccionado(municipio: Municipio): void {
    this.data.cdmunicipioEntrega = municipio.cdmunicipio || '';
    this.data.dsmunicipioEntrega = municipio.dsmunicipio || '';
    this.data.cdsuraEntrega = municipio.cdsura || ''; // Guardar CDSURA para búsqueda de poblaciones
    // Limpiar población al cambiar municipio
    this.limpiarPoblacion();
    this.emitChange();
    this.messageService.add({
      severity: 'success',
      summary: 'Municipio entrega seleccionado',
      detail: municipio.dsmunicipio || '',
      life: 2000
    });
  }

  /**
   * Busca poblaciones disponibles según municipios seleccionados.
   * Legacy: siBuscarPoblacion -> busca en W094_SGS_TARIFAS_TRANSPORTE usando CDSURA
   */
  buscarPoblacion(): void {
    if (!this.data.cdsuraOrigen || !this.data.cdsuraEntrega) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atención',
        detail: 'Debe seleccionar primero los municipios de origen y entrega',
        life: 3000
      });
      return;
    }
    this.buscadorPoblacion.abrir();
  }

  /**
   * Maneja selección de población.
   * Legacy: Solo guarda código y nombre. NO asigna tarifa aquí.
   * La tarifa se asigna cuando cambia Pago Transporte a SI.
   */
  onPoblacionSeleccionada(poblacion: Poblacion): void {
    this.data.cdpoblacion = poblacion.cdpoblacion || '';
    this.data.dspoblacion = poblacion.dspoblacion || '';
    // Guardar tarifa para cuando se marque Pago Transporte = SI
    this.tarifaPoblacion = poblacion.ptcosto ?? null;

    // Si Pago Transporte ya está en SI, calcular valor
    if (this.data.snpagoTransporteEmpleado === 'S') {
      this.data.ptvalorTransporte = this.tarifaPoblacion;
    }

    this.emitChange();
    this.messageService.add({
      severity: 'success',
      summary: 'Población seleccionada',
      detail: `${poblacion.cdpoblacion} - ${poblacion.dspoblacion}`,
      life: 2000
    });
  }

  /**
   * Maneja cambio en Pago Transporte a Empleado.
   * Legacy: calcularValorTransporte() en CrearCitaAdministrativaViewImpl.java
   * - Si SI y hay municipios: busca tarifa
   * - Si NO: limpia valor
   */
  onPagoTransporteEmpleadoChange(): void {
    if (this.data.snpagoTransporteEmpleado === 'S') {
      // Validar que haya municipios seleccionados
      if (!this.data.cdmunicipioOrigen || !this.data.cdmunicipioEntrega) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Atención',
          detail: 'Por favor seleccione los municipios',
          life: 3000
        });
        this.data.snpagoTransporteEmpleado = 'N';
        return;
      }
      // Asignar tarifa guardada de la población
      this.data.ptvalorTransporte = this.tarifaPoblacion;
    } else {
      // Pago = NO: limpiar valor transporte
      this.data.ptvalorTransporte = null;
    }
    this.emitChange();
  }

  /**
   * Maneja cambio en transporte adicional.
   * Legacy: Limpia valores cuando se cambia a NO
   */
  onTransporteAdicionalChange(): void {
    if (this.data.snpagoTransporteAdicional === 'N') {
      this.data.ptvalorTransporteAdicional = null;
      this.data.dsobservacionTransporteAdicional = '';
    }
    this.emitChange();
  }

  /** Limpia datos de población y tarifa */
  private limpiarPoblacion(): void {
    this.data.cdpoblacion = '';
    this.data.dspoblacion = '';
    this.data.ptvalorTransporte = null;
    this.tarifaPoblacion = null;
  }

  /** Emite cambio de datos */
  emitChange(): void {
    this.dataChange.emit(this.data);
  }
}
